import React, { useState, useEffect } from 'react'
import { UserFormModalMetaProps, User, Event } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { API_BASE_URL, ROLE_MAP } from '../data'

type Props = {
    meta: UserFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (userData: any) => Promise<void>
    initialuser?: User | null
}

export const UserFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialuser } = props

    const [formData, setFormData] = useState({
        volunteer_csid: '',
        name: '',
        username: '',
        email: '',
        role: ''
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number>(null);


    // const fetchEvents = async () => {
    //     try {
    //         setLoading(true);
    //         const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' });
    //         if (!response.ok) {
    //             throw new Error(`HTTP error! status: ${response.status}`)
    //         }
    //         const result = await response.json()
    //         setEvents(result);
    //     }
    //     catch (err) {
    //         setError(err instanceof Error ? err.message : "Failed to fetch events");
    //     }
    //     finally {
    //         setLoading(false);
    //     }
    // }

    // Populate form when editing an event
    useEffect(() => {
        if (initialuser && isOpen) {
            setFormData({
                volunteer_csid: initialuser.volunteer_csid.toString(),
                name: initialuser.name,
                username: initialuser.username,
                email: initialuser.email,
                role: initialuser.role
            })
        }
        else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                volunteer_csid: '',
                name: '',
                username: '',
                email: '',
                role: ''
            })
            setError(null)
        }
    }, [initialuser, isOpen])


    // useEffect(() => {
    //     if (formData.role === 'checkin_volunteer') {
    //         fetchEvents();
    //     }
    // }, [formData.role]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            // Format time fields to ensure they're in HH:MM format (remove seconds if present)
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="user-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="user-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        {/* User Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.namePlaceholder}
                            />
                        </div>
                        {/* Username */}
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.usernameLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                required
                                disabled={initialuser ? true : false}
                                value={formData.username}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.usernamePlaceholder}
                            />
                        </div>
                        {/* User Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.emailLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.emailPlaceholder}
                            />
                        </div>
                        {/* User Role */}
                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.roleLabel} <span className="text-danger-500">*</span>
                            </label>
                            <select
                                id="role"
                                name="role"
                                required
                                value={formData.role}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="" disabled>{meta.rolePlaceholder}</option>
                                {Object.entries(ROLE_MAP).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* {formData.role === 'checkin_volunteer' && (
                            <div className="col-span-2"> 
                                <label htmlFor="event" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Event <span className="text-danger-500">*</span>
                                </label>
                                {loading && events.length === 0 ? (
                                    <div className="text-sm text-gray-500">Loading events...</div>
                                ) : (
                                    <select
                                        id="event"
                                        name="event"
                                        required
                                        value={selectedEventId}
                                        onChange={(e) => { setSelectedEventId(Number(e.target.value)) }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    >
                                        <option value="" disabled>Select Event</option>
                                        {events.map((event) => (
                                            <option key={event.id} value={event.id}>
                                                {event.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )} */}

                    </div>
                </div>
            </form>
        </Modal >
    )
}
